// Zod 4 schemas — the single source of truth for /api/v1 request + response
// shapes. Mirrors the data model in src/lib/placeModel.ts. Importing
// "zod-openapi/extend" (here, via the side-effecting import below) wires up
// TypeScript support for the OpenAPI-specific keys in `.meta()` (id, param, …)
// and lets `createDocument` pick up any schema tagged with `.meta({ id })`
// as a reusable component.
//
// zod-openapi v5 needs Zod 4 ("zod/v4" or plain "zod" on 4.x). We import the
// plain "zod" (project is on zod 4.4.3) and import "zod-openapi" once so the
// `.meta()` type augmentation is active.
import { z } from "zod";
import "zod-openapi"; // side-effect: augments z.*.meta() with OpenAPI keys

// ---------------------------------------------------------------------------
// Enum schemas (mirror the unions in placeModel.ts)
// ---------------------------------------------------------------------------

export const PlaceTypeSchema = z
  .enum(["cafe", "food", "bar"])
  .meta({ id: "PlaceType", description: "Primary venue type." });

export const PlaceFormatSchema = z
  .enum([
    "Cafe",
    "Sit-down",
    "Rooftop",
    "Bar-with-food",
    "Mall restaurant",
    "Hotel restaurant",
    "Market",
    "Food court",
    "Fine dining",
    "Street food",
  ])
  .meta({ id: "PlaceFormat" });

export const PriceTierSchema = z
  .enum(["$", "$$", "$$$", "$$$$"])
  .meta({ id: "PriceTier" });

export const NoiseLevelSchema = z
  .enum(["quiet", "moderate", "buzzy"])
  .meta({ id: "NoiseLevel" });

export const VibeTagSchema = z
  .enum([
    "photo-first",
    "heritage",
    "industrial",
    "garden",
    "club-vibe",
    "zen-calm",
    "casual no-frills",
    "chic",
    "minimalist",
    "cozy",
  ])
  .meta({ id: "VibeTag" });

export const GoodForTagSchema = z
  .enum([
    "work",
    "brunch",
    "date",
    "group dinner",
    "sunset",
    "late night",
    "lunch",
    "special occasion",
    "photo",
    "quick bite",
    "drinks",
    "solo",
    "kids",
  ])
  .meta({ id: "GoodForTag" });

export const CrowdTagSchema = z
  .enum([
    "locals",
    "tourists",
    "expats",
    "families",
    "business",
    "influencers",
    "young-creative",
    "older-traditional",
    "specific-community",
  ])
  .meta({ id: "CrowdTag" });

export const MichelinStatusSchema = z
  .enum(["3-star", "2-star", "1-star", "Bib Gourmand", "Plate", "Selected", "none"])
  .meta({ id: "MichelinStatus" });

export const TouristRatioSchema = z
  .enum(["mostly-local", "mixed", "mostly-tourist"])
  .meta({ id: "TouristRatio" });

export const OperatingStatusSchema = z
  .enum(["open", "temporarily-closed", "permanently-closed"])
  .meta({ id: "OperatingStatus" });

export const QualityFlagSchema = z
  .enum([
    "food-safety-issue",
    "declined-quality",
    "persistent-service-complaints",
    "tourist-trap-pattern",
    "rustic-hygiene",
  ])
  .meta({ id: "QualityFlag" });

export const ReservationsPolicySchema = z
  .enum(["required", "recommended", "walk-in"])
  .meta({ id: "ReservationsPolicy" });

export const CashPolicySchema = z
  .enum(["cash-only", "mixed", "cashless-friendly"])
  .meta({ id: "CashPolicy" });

export const VenueKindSchema = z
  .enum(["cafes", "food", "both"])
  .meta({ id: "VenueKind", description: "Browsing bucket. 'food' also includes bars." });

// Sort keys accepted by GET /places.
export const SortKeySchema = z
  .enum(["recommended", "nearest", "best-value", "quietest", "best-wifi"])
  .meta({ id: "SortKey" });

// ---------------------------------------------------------------------------
// Nested object schemas
// ---------------------------------------------------------------------------

export const CafeScoresSchema = z
  .object({
    environment: z.number(),
    wifi: z.number(),
    outlets: z.number(),
    price: z.number(),
    noise: z.number(),
  })
  .meta({ id: "CafeScores", description: "The five composite-score categories (each 0-5)." });

export const CreatorClipSchema = z
  .object({
    url: z.string(),
    creator: z.string(),
    platform: z.literal("tiktok"),
    note: z.string(),
    gifUrl: z.string().optional(),
    publishedAt: z.string().optional(),
  })
  .meta({ id: "CreatorClip", description: "A single creator clip (TikTok)." });

export const ViralMetadataSchema = z
  .object({
    isViral: z.boolean(),
    creatorCount: z.number(),
    viralSince: z.string().nullable(),
    primaryNote: z.string(),
  })
  .meta({ id: "ViralMetadata" });

// hoursByDay: keys "0".."6" (Sun..Sat), value is [open, close] or null.
export const DayHoursSchema = z.tuple([z.number(), z.number()]).nullable();
export const HoursByDaySchema = z
  .record(z.enum(["0", "1", "2", "3", "4", "5", "6"]), DayHoursSchema)
  .meta({ id: "HoursByDay", description: "Per-day [open,close] overrides; key 0=Sun..6=Sat, null=closed." });

// ---------------------------------------------------------------------------
// Place — mirror the Place interface faithfully. Optional fields .optional().
// ---------------------------------------------------------------------------

export const PlaceSchema = z
  .object({
    // Identity & location
    id: z.string(),
    sourceIds: z.record(PlaceTypeSchema, z.string()).optional(),
    types: z.array(PlaceTypeSchema),
    name: z.string(),
    area: z.string(),
    zone: z
      .string()
      .optional()
      .meta({ description: "Curated zone (groups granular areas); matches the city's zone chips. Falls back to area." }),
    city: z.string(),
    address: z.string(),
    lat: z.number(),
    lng: z.number(),

    // Content
    hours: z.string(),
    openHour: z.number(),
    closeHour: z.number(),
    hoursByDay: HoursByDaySchema.optional(),
    photo: z.string(),
    photos: z.array(z.string()).optional(),
    rating: z.number(),
    vibe: z.string(),
    nomadTip: z.string(),
    description: z.string().optional(),
    googlePlaceId: z.string().optional(),
    heroPhotoRef: z.string().optional(),
    coverAssetUrl: z.string().optional(),
    googleMapsUrl: z.string().optional(),
    appleMapsUrl: z.string().optional(),
    amapUrl: z.string().optional(),
    clips: z.array(CreatorClipSchema).optional(),

    // Legacy (optional)
    wifi: z.union([z.number(), z.boolean()]).optional(),
    noise: z.enum(["Quiet", "Moderate", "Buzzy"]).optional(),
    amenities: z.array(z.string()).optional(),
    scores: CafeScoresSchema.optional(),
    tier: z.union([z.literal(1), z.literal(2)]).optional(),
    website: z.string().optional(),
    cuisine: z.union([z.string(), z.array(z.string())]).optional(),
    mealType: z.string().optional(),
    occasion: z.string().optional(),
    groupSize: z.string().optional(),
    priceTier: z.union([z.string(), PriceTierSchema]).optional(),
    signature: z.string().optional(),
    viralSince: z.string().optional(),
    creatorCount: z.number().optional(),
    viralAbout: z.string().optional(),

    // Universal-protocol fields (new)
    neighborhood: z.string().optional(),
    format: PlaceFormatSchema.optional(),
    brandId: z.string().nullable().optional(),
    reservations: ReservationsPolicySchema.optional(),
    cashPolicy: CashPolicySchema.optional(),
    transitProximity: z.array(z.string()).optional(),
    outdoorSeating: z.boolean().optional(),
    outlets: z.boolean().optional(),
    noiseLevel: NoiseLevelSchema.optional(),
    michelinStatus: MichelinStatusSchema.optional(),
    editorialSources: z.array(z.string()).optional(),
    crossSourceCount: z.number().optional(),
    topSources: z.array(z.string()).optional(),
    touristRatio: TouristRatioSchema.optional(),
    crowdTags: z.array(CrowdTagSchema).optional(),
    vibeTags: z.array(VibeTagSchema).optional(),
    goodFor: z.array(GoodForTagSchema).optional(),
    operatingStatus: OperatingStatusSchema.optional(),
    lastVerified: z.string().optional(),
    qualityFlags: z.array(QualityFlagSchema).optional(),
    estReviewCount: z.number().optional(),
    viralMetadata: ViralMetadataSchema.optional(),
  })
  .meta({ id: "Place", description: "A venue in the citybrews dataset." });

// Place enriched with computed fields, returned by GET /places/{id}.
export const PlaceDetailSchema = PlaceSchema.extend({
  score: z.number().nullable().meta({ description: "Composite citybrews score (avg of 5 categories), or null if ungraded." }),
  isOpenNow: z.boolean().meta({ description: "Open right now in the venue's local timezone." }),
}).meta({ id: "PlaceDetail" });

// ---------------------------------------------------------------------------
// City / zone / region
// ---------------------------------------------------------------------------

export const CitySummarySchema = z
  .object({
    name: z.string(),
    country: z.string(),
    flag: z.string(),
    timezone: z.string(),
    photo: z.string().optional(),
    center: z.tuple([z.number(), z.number()]).meta({ description: "[lng, lat]" }),
    zoom: z.number(),
    counts: z.object({
      total: z.number(),
      cafes: z.number(),
      food: z.number(),
      bars: z.number(),
    }),
  })
  .meta({ id: "CitySummary" });

export const ZoneSchema = z
  .object({
    zone: z.string(),
    count: z.number(),
  })
  .meta({ id: "Zone" });

export const RegionSchema = z
  .object({
    city: z.string(),
    zone: z.string(),
    count: z.number(),
  })
  .meta({ id: "Region", description: "A zone scoped to a city (cross-city zone listing)." });

export const CityDetailSchema = CitySummarySchema.extend({
  zones: z.array(ZoneSchema).meta({ description: "Curated zones for this city, with counts." }),
}).meta({ id: "CityDetail" });

// A real-world city from the gazetteer (GeoNames), returned by /city-suggest to
// validate + canonicalize a nomination query. NOT a covered city — those come
// from /cities. `flag` is a derived regional-indicator emoji from the ISO code.
export const CitySuggestionSchema = z
  .object({
    name: z.string().meta({ description: "Canonical city name (e.g. 'Tokyo')." }),
    country: z.string().meta({ description: "Country name (e.g. 'Japan')." }),
    countryCode: z.string().meta({ description: "ISO 3166-1 alpha-2 code." }),
    flag: z.string().meta({ description: "Flag emoji derived from the country code." }),
    population: z.number(),
  })
  .meta({ id: "CitySuggestion" });

// ---------------------------------------------------------------------------
// Meta / stats / health / clips
// ---------------------------------------------------------------------------

export const MetaSchema = z
  .object({
    placeTypes: z.array(z.string()),
    placeFormats: z.array(z.string()),
    priceTiers: z.array(z.string()),
    noiseLevels: z.array(z.string()),
    vibeTags: z.array(z.string()),
    goodForTags: z.array(z.string()),
    crowdTags: z.array(z.string()),
    michelinStatuses: z.array(z.string()),
    touristRatios: z.array(z.string()),
    operatingStatuses: z.array(z.string()),
    qualityFlags: z.array(z.string()),
    venueKinds: z.array(z.string()),
    sortKeys: z.array(z.string()),
    amenityKeys: z.array(z.string()),
    noiseOptions: z.array(z.string()),
    amenityOptions: z.array(z.string()),
    scoreCategories: z.array(z.object({ key: z.string(), label: z.string() })),
  })
  .meta({ id: "Meta", description: "Controlled vocabularies for building queries." });

export const StatsSchema = z
  .object({
    totalPlaces: z.number(),
    totalCities: z.number(),
    byType: z.object({
      cafe: z.number(),
      food: z.number(),
      bar: z.number(),
    }),
    withClips: z.number(),
    totalClips: z.number(),
    scored: z.number(),
    byCity: z.array(z.object({ city: z.string(), count: z.number() })),
  })
  .meta({ id: "Stats" });

export const HealthSchema = z
  .object({
    status: z.literal("ok"),
    version: z.string(),
    places: z.number(),
    cities: z.number(),
    timestamp: z.string(),
  })
  .meta({ id: "Health" });

export const ClipsResponseSchema = z
  .object({
    placeId: z.string(),
    clips: z.array(CreatorClipSchema),
    viralMetadata: ViralMetadataSchema.nullable(),
  })
  .meta({ id: "ClipsResponse" });

// ---------------------------------------------------------------------------
// Envelope helpers
// ---------------------------------------------------------------------------

export const MetaEnvelopeSchema = z
  .object({
    total: z.number().optional(),
    count: z.number().optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
  })
  .meta({ id: "EnvelopeMeta" });

/**
 * Wrap a payload schema in the success envelope: { data, meta? }.
 * Use at the API boundary to describe a 200 response body.
 */
export function okEnvelope<T extends z.ZodTypeAny>(dataSchema: T) {
  return z.object({
    data: dataSchema,
    meta: MetaEnvelopeSchema.optional(),
  });
}

export const ErrorSchema = z
  .object({
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.unknown().optional(),
    }),
  })
  .meta({ id: "Error", description: "Standard error envelope." });

// ---------------------------------------------------------------------------
// PlaceQuery — coerces/validates every GET /places query param.
// ---------------------------------------------------------------------------

// Split a comma-separated string into a trimmed, non-empty string array.
const csv = (val: string): string[] =>
  val
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

// A "csv -> string[]" param that also accepts already-array input.
const csvArray = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((v): string[] | undefined => {
    if (v === undefined) return undefined;
    if (Array.isArray(v)) return v;
    return csv(v);
  });

const priceTierArray = csvArray.pipe(z.array(PriceTierSchema).optional());
const goodForArray = csvArray.pipe(z.array(GoodForTagSchema).optional());
const formatArray = csvArray.pipe(z.array(PlaceFormatSchema).optional());

// Boolean coercion that treats "true"/"1"/"yes" as true (z.coerce.boolean
// treats any non-empty string as true, including "false", so do it manually).
const boolParam = z
  .union([z.string(), z.boolean()])
  .optional()
  .transform((v): boolean | undefined => {
    if (v === undefined) return undefined;
    if (typeof v === "boolean") return v;
    return ["true", "1", "yes", "on"].includes(v.toLowerCase());
  });

export const PlaceQuerySchema = z.object({
  // Scope
  kind: VenueKindSchema.optional(),
  city: z.string().optional(),
  zone: z.string().optional(),
  types: csvArray, // PlaceType[] (cafe/food/bar) — substring-free, exact match
  q: z.string().optional(), // free-text substring over name/area/city/address

  // Attribute filters
  noise: z.string().optional(), // "quiet" | "moderate" | "buzzy" (case-insensitive)
  amenities: csvArray, // legacy UI amenity labels (matchesAmenities)
  cuisine: csvArray, // case/accent-folded substring OR over string|string[] cuisine
  priceTier: priceTierArray, // PriceTier[] (OR)
  goodFor: goodForArray, // GoodForTag[] (OR)
  format: formatArray, // PlaceFormat[] (OR)
  touristRatio: TouristRatioSchema.optional(), // exact match
  // Mood keys (work|cafe|food|drinks|night|views|todo). When present the query
  // filters the WHOLE city by mood (OR-match) — ignoring bbox — and orders by
  // mood priority (first key first) then score. Powers whole-city mood filtering.
  moods: csvArray,
  openNow: boolParam,
  minScore: z.coerce.number().min(0).max(5).optional(),
  hasClips: boolParam,

  // Geo
  bbox: z
    .string()
    .optional()
    .transform((v): [number, number, number, number] | undefined => {
      if (!v) return undefined;
      const parts = v.split(",").map((s) => Number(s.trim()));
      if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return undefined;
      return [parts[0], parts[1], parts[2], parts[3]]; // [west, south, east, north]
    }),
  near: z
    .string()
    .optional()
    .transform((v): [number, number] | undefined => {
      if (!v) return undefined;
      const parts = v.split(",").map((s) => Number(s.trim()));
      if (parts.length !== 2 || parts.some((n) => !Number.isFinite(n))) return undefined;
      return [parts[0], parts[1]]; // [lat, lng]
    }),
  radius: z.coerce.number().positive().optional(), // km; used with near

  // Sort + pagination
  sort: SortKeySchema.default("recommended"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

// ---------------------------------------------------------------------------
// Inferred TS types (API-boundary types — distinct from the runtime Place
// interface in placeModel.ts; repository keeps using the real Place).
// ---------------------------------------------------------------------------

export type ApiPlace = z.infer<typeof PlaceSchema>;
export type ApiPlaceDetail = z.infer<typeof PlaceDetailSchema>;
export type ApiCafeScores = z.infer<typeof CafeScoresSchema>;
export type ApiCreatorClip = z.infer<typeof CreatorClipSchema>;
export type ApiViralMetadata = z.infer<typeof ViralMetadataSchema>;
export type ApiHoursByDay = z.infer<typeof HoursByDaySchema>;
export type CitySummary = z.infer<typeof CitySummarySchema>;
export type CityDetail = z.infer<typeof CityDetailSchema>;
export type CitySuggestion = z.infer<typeof CitySuggestionSchema>;
export type Zone = z.infer<typeof ZoneSchema>;
export type Region = z.infer<typeof RegionSchema>;
export type Meta = z.infer<typeof MetaSchema>;
export type Stats = z.infer<typeof StatsSchema>;
export type Health = z.infer<typeof HealthSchema>;
export type ClipsResponse = z.infer<typeof ClipsResponseSchema>;
export type EnvelopeMeta = z.infer<typeof MetaEnvelopeSchema>;
export type ApiError = z.infer<typeof ErrorSchema>;

// PlaceQuery: OUTPUT type (post-transform) — what the repository consumes.
export type PlaceQuery = z.output<typeof PlaceQuerySchema>;
// Raw input shape (pre-transform), occasionally useful for callers.
export type PlaceQueryInput = z.input<typeof PlaceQuerySchema>;
